import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Eye, EyeOff, Lock, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import styled from 'styled-components';
import { ThemeProvider } from 'styled-components';
import { Toaster } from 'react-hot-toast';
import { GlobalStyles } from './styles/GlobalStyles';
import { theme } from './styles/theme';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { Layout } from './components/ui/Layout';
import { Form, FormGroup, Label, Input, Button, ErrorMessage } from './components/ui/Form';
import { authService } from './services/authService';

const InputContainer = styled.div`
  position: relative;
`;

const InputIcon = styled.div`
  position: absolute;
  left: ${theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.textTertiary};
  z-index: 1;
`;

const StyledInput = styled(Input)`
  padding-left: ${theme.spacing['3xl']};
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: ${theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${theme.colors.textTertiary};
  cursor: pointer;
  padding: ${theme.spacing.xs};
  border-radius: ${theme.borderRadius.sm};
  
  &:hover {
    color: ${theme.colors.textPrimary};
    background-color: ${theme.colors.gray50};
  }
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl} 0;
`;

const ErrorIcon = styled.div`
  width: 64px;
  height: 64px;
  background-color: ${theme.colors.error};
  border-radius: ${theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${theme.spacing.lg};
  color: ${theme.colors.white};
`;

const ErrorTitle = styled.h3`
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const ErrorDescription = styled.p`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.textSecondary};
  margin: 0 0 ${theme.spacing.xl} 0;
  line-height: 1.5;
`;

const validationSchema = yup.object({
  password: yup
    .string()
    .required('请输入密码')
    .min(6, '密码至少 6 位'),
  confirmPassword: yup
    .string()
    .required('请再次输入密码')
    .oneOf([yup.ref('password')], '两次输入的密码不一致'),
});

interface ConfirmPasswordForm {
  password: string;
  confirmPassword: string;
}

const ConfirmPassword: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidId, setIsValidId] = useState<boolean | null>(null);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ConfirmPasswordForm>({
    resolver: yupResolver(validationSchema),
  });

  useEffect(() => {
    if (!id) {
      setIsValidId(false);
      return;
    }
    setIsValidId(true);
  }, [id]);

  const onSubmit = async (data: ConfirmPasswordForm) => {
    if (!id) return;
    setIsLoading(true);
    try {
      const response = await authService.confirmResetPassword(id, data.password, data.confirmPassword);
      if (response.success) {
        toast.success('密码设置成功，请登录。');
        navigate('/login');
      } else {
        toast.error(response.error || '设置失败，请稍后重试');
      }
    } catch (error) {
      toast.error('系统繁忙，请稍后再试');
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidId === null) {
    return (
      <Layout
        title="设置密码"
        subtitle="正在验证链接..."
      >
        <div style={{ textAlign: 'center', padding: `${theme.spacing.xl} 0` }}>
          <div>加载中...</div>
        </div>
      </Layout>
    );
  }

  if (isValidId === false) {
    return (
      <Layout
        title="链接已失效"
        subtitle="设置链接无效或已过期"
      >
        <ErrorContainer>
          <ErrorIcon>
            <AlertCircle size={32} />
          </ErrorIcon>
          <ErrorTitle>链接无效或已过期</ErrorTitle>
          <ErrorDescription>
            该设置链接已失效，请联系管理员重新发送。
          </ErrorDescription>
          <Button as="a" href="/sso/login" $fullWidth>
            返回登录
          </Button>
        </ErrorContainer>
      </Layout>
    );
  }

  return (
    <Layout
      title="设置密码"
      subtitle="请为账号设置新密码"
    >
      <Form onSubmit={handleSubmit(onSubmit)}>
        <FormGroup>
          <Label htmlFor="password">新密码</Label>
          <InputContainer>
            <InputIcon>
              <Lock size={20} />
            </InputIcon>
            <StyledInput
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="请输入新密码"
              $hasError={!!errors.password}
              {...register('password')}
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </PasswordToggle>
          </InputContainer>
          {errors.password && (
            <ErrorMessage>
              <AlertCircle size={16} />
              {errors.password.message}
            </ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="confirmPassword">确认新密码</Label>
          <InputContainer>
            <InputIcon>
              <Lock size={20} />
            </InputIcon>
            <StyledInput
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="请再次输入新密码"
              $hasError={!!errors.confirmPassword}
              {...register('confirmPassword')}
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </PasswordToggle>
          </InputContainer>
          {errors.confirmPassword && (
            <ErrorMessage>
              <AlertCircle size={16} />
              {errors.confirmPassword.message}
            </ErrorMessage>
          )}
        </FormGroup>

        <Button 
          type="submit" 
          $fullWidth 
          $loading={isLoading}
          disabled={isLoading}
        >
          {isLoading ? '正在保存...' : '确认设置'}
        </Button>
      </Form>
    </Layout>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <Router basename="/sso">
        <Routes>
          {/* Default redirect to login */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          
          {/* Authentication routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/confirm/:id" element={<ConfirmPassword />} />
          
          {/* Catch all route - redirect to login */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
      
      {/* Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: theme.colors.surface,
            color: theme.colors.textPrimary,
            boxShadow: theme.shadows.lg,
            borderRadius: theme.borderRadius.lg,
            fontSize: theme.fontSizes.sm,
            maxWidth: '400px',
          },
          success: {
            iconTheme: {
              primary: theme.colors.success,
              secondary: theme.colors.white,
            },
          },
          error: {
            iconTheme: {
              primary: theme.colors.error,
              secondary: theme.colors.white,
            },
          },
        }}
      />
    </ThemeProvider>
  );
};

export default App;
